import type { ReactNode } from 'react';
import { CaretDownIcon, CaretRightIcon } from '@phosphor-icons/react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Badge } from 'src/components/ui/badge';
import { cn } from 'src/lib/utils';

export function PanelSectionCollapsible({
  title,
  open,
  onOpenChange,
  badge,
  children,
  className,
  contentClassName,
}: {
  title: ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  badge?: ReactNode;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
}) {
  return (
    <Collapsible
      open={open}
      onOpenChange={onOpenChange}
      className={cn('border-b last:border-b-0', className)}
    >
      <CollapsibleTrigger className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-sm font-medium hover:bg-muted/50 transition-colors">
        {open ? (
          <CaretDownIcon className="size-4 shrink-0 text-muted-foreground" />
        ) : (
          <CaretRightIcon className="size-4 shrink-0 text-muted-foreground" />
        )}
        <span className="min-w-0 flex-1 truncate">{title}</span>
        {badge != null ? (
          <span className="flex shrink-0 items-center gap-1">{badge}</span>
        ) : null}
      </CollapsibleTrigger>
      <CollapsibleContent
        className={cn(
          'max-h-[min(40vh,320px)] overflow-y-auto border-t bg-muted/20',
          contentClassName,
        )}
      >
        <div className="p-3">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function PanelSectionBadge({
  children,
  variant = 'secondary',
}: {
  children: ReactNode;
  variant?: 'secondary' | 'outline';
}) {
  return (
    <Badge variant={variant} className="h-5 px-1.5 text-[10px] font-normal">
      {children}
    </Badge>
  );
}
